import type { ChangeEvent, KeyboardEvent } from 'react'

import {
  formatNumberDraft,
  isGroupingChar,
  mapCaret,
  parseNumberDraft,
  type DraftRules,
} from '@/utils/numberInput'

interface Options {
  /** The draft — a plain `1234.5` string, `''` when empty. */
  value: string
  onChange: (draft: string) => void
  min?: number
  max?: number
  step?: string | number
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
}

function stepSize(step: string | number | undefined): number | null {
  const size = Number(step)
  return step === undefined || step === 'any' || !(size > 0) ? null : size
}

function decimals(value: number): number {
  return (String(value).split('.')[1] ?? '').length
}

/**
 * Props for a text input that edits a number with the user's thousand and
 * decimal separators as they type. A native `type="number"` cannot show
 * grouping, so this formats a plain text field instead and keeps the caret
 * where the user left it. The arrow keys step the value as they would natively.
 */
export function useLocalizedNumberInput({ value, onChange, min, max, step, onKeyDown }: Options) {
  const size = stepSize(step)
  const rules: DraftRules = {
    allowNegative: min === undefined || min < 0,
    allowDecimal: size === null || !Number.isInteger(size),
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target
    const raw = el.value
    const caret = el.selectionStart ?? raw.length
    const draft = parseNumberDraft(raw, rules)
    const formatted = formatNumberDraft(draft)
    // Writing the formatted text now means React finds nothing to change on
    // re-render, so it leaves the caret where we put it.
    el.value = formatted
    const pos = mapCaret(raw, caret, formatted)
    el.setSelectionRange(pos, pos)
    onChange(draft)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e)
    if (e.defaultPrevented) return
    const el = e.currentTarget
    const start = el.selectionStart
    const collapsed = start !== null && start === el.selectionEnd
    // Deleting a separator alone would only see it put back; take the digit beside it.
    if (collapsed && e.key === 'Backspace' && isGroupingChar(el.value[start - 1])) {
      el.setSelectionRange(start - 1, start - 1)
    } else if (collapsed && e.key === 'Delete' && isGroupingChar(el.value[start])) {
      el.setSelectionRange(start + 1, start + 1)
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const by = size ?? 1
      const current = Number(value) || 0
      let next = current + (e.key === 'ArrowUp' ? by : -by)
      if (min !== undefined) next = Math.max(min, next)
      if (max !== undefined) next = Math.min(max, next)
      next = Number(next.toFixed(Math.max(decimals(by), decimals(current))))
      onChange(String(next))
    }
  }

  return {
    type: 'text' as const,
    inputMode: rules.allowDecimal ? ('decimal' as const) : ('numeric' as const),
    autoComplete: 'off',
    value: formatNumberDraft(value),
    onChange: handleChange,
    onKeyDown: handleKeyDown,
  }
}
